import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { AssistantDock } from '../../features/assistant/ui/AssistantDock';
import { AssistantProvider } from '../../features/assistant/model/assistant-chat-context';
import { BugReportProvider } from '../../features/bug-report/model/bug-report-context';
import { BugReportButton } from '../../features/bug-report/ui/BugReportButton';
import { StaffCommandPalette } from '../../widgets/command-palette/StaffCommandPalette';
import { UnsavedChangesGuard } from '../../widgets/unsaved-changes/UnsavedChangesGuard';
import { StaffOrgTariffLifecycleShellBanner } from '../../widgets/staff-shell/StaffOrgTariffLifecycleShellBanner';
import { StaffSidebar } from '../../widgets/sidebar/staff-sidebar';
import { ErrorBoundary } from '../../shared/diagnostics/ErrorBoundary';
import { installDiagnostics, recordRoute } from '../../shared/diagnostics/diagnostics-store';
import { setDiagnosticsAuthSnapshot } from '../../shared/diagnostics/bug-context';
import { StaffTopbar } from '../../widgets/topbar/staff-topbar';
import { isRouteAllowed } from '../../shared/access/access';
import { useAuth } from '../../shared/auth/use-auth';
import { STAFF_SCROLL_LOCK_ATTR } from './staff-scroll-lock';
import { useStaffRouteTransitions } from './use-staff-route-transitions';
import styles from './AppShell.module.css';

const SIDEBAR_COLLAPSED_KEY = 'domcrm-staff-sidebar-collapsed';
const MOBILE_SHELL_MEDIA_QUERY = '(max-width: 767px)';

function readSidebarCollapsed(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function readMobileViewport(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(MOBILE_SHELL_MEDIA_QUERY).matches
    );
  } catch {
    return false;
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileViewport, setMobileViewport] = useState(readMobileViewport);
  const { me, impersonateOrgId } = useAuth();
  /** Облако помощника — только тем, кому доступен раздел «Помощник» (право assistant:use по данным сервера). */
  const assistantAvailable = isRouteAllowed(me?.capabilities, '/assistant');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const mainRef = useRef<HTMLElement>(null);

  useStaffRouteTransitions(mainRef);

  const closeMobile = useCallback(() => setMobileNavOpen(false), []);
  const toggleMobile = useCallback(() => setMobileNavOpen((open) => !open), []);
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((c) => !c);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(MOBILE_SHELL_MEDIA_QUERY);
    const onChange = () => setMobileViewport(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!mobileViewport) setMobileNavOpen(false);
  }, [mobileViewport]);

  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    installDiagnostics();
  }, []);

  useEffect(() => {
    recordRoute(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    setDiagnosticsAuthSnapshot({
      user_id: me?.id ?? null,
      role: me?.role ?? null,
      org_id: me?.organization_id ?? null,
      branch_id: me?.active_branch_id ?? null,
      impersonation_org_id: impersonateOrgId
    });
  }, [me, impersonateOrgId]);


  /** POP (browser/Android Back) закрывает transient-меню, не оставляя drawer поверх нового route. */
  useEffect(() => {
    if (navigationType !== 'POP') return;
    setMobileNavOpen(false);
  }, [location.key, navigationType]);

  useLayoutEffect(() => {
    if (!mobileNavOpen) return;
    const main = mainRef.current;
    const scrollingElement = document.scrollingElement;
    const saved = {
      main: main?.scrollTop ?? 0,
      viewport: scrollingElement?.scrollTop ?? window.scrollY ?? 0
    };
    const pathnameAtLock = window.location.pathname;
    const previousOverflow = document.body.style.overflow;

    // Маркер для менеджера переходов: временный scroll lock не должен сохранять сброшенный 0.
    document.documentElement.setAttribute(STAFF_SCROLL_LOCK_ATTR, 'true');
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.removeAttribute(STAFF_SCROLL_LOCK_ATTR);
      document.body.style.overflow = previousOverflow;
      // При переходе на другой route позицию восстановит менеджер переходов (PUSH/POP).
      if (window.location.pathname !== pathnameAtLock) return;
      if (scrollingElement && scrollingElement.scrollTop !== saved.viewport) {
        scrollingElement.scrollTop = saved.viewport;
      } else if (
        saved.viewport !== 0 &&
        window.scrollY !== saved.viewport &&
        typeof window.scrollTo === 'function'
      ) {
        window.scrollTo(0, saved.viewport);
      }
      if (main && main.scrollTop !== saved.main) main.scrollTop = saved.main;
    };
  }, [mobileNavOpen]);

  return (
    <BugReportProvider>
      <ErrorBoundary>
        <AssistantProvider enabled={assistantAvailable}>
          <div className={`domcrm-ui-root ${styles.root}`}>
            <StaffCommandPalette />
            <StaffTopbar
              onNavToggle={toggleMobile}
              navOpen={mobileNavOpen}
              sidebarCollapsed={sidebarCollapsed}
              onSidebarCollapseToggle={toggleSidebarCollapsed}
            />
            <StaffOrgTariffLifecycleShellBanner />
            <div className={styles.body}>
              {mobileNavOpen ? (
                <button
                  type="button"
                  className={styles.backdrop}
                  aria-label="Закрыть меню навигации"
                  onClick={closeMobile}
                />
              ) : null}
              <StaffSidebar
                mobileOpen={mobileNavOpen}
                mobileDrawer={mobileViewport}
                onMobileClose={closeMobile}
                collapsed={sidebarCollapsed}
              />
              <main ref={mainRef} className={styles.main} tabIndex={-1} data-testid="staff-route-main">
                {children}
              </main>
            </div>
            {assistantAvailable ? <AssistantDock /> : null}
            <BugReportButton assistantAvailable={assistantAvailable} />
            <UnsavedChangesGuard />
          </div>
        </AssistantProvider>
      </ErrorBoundary>
    </BugReportProvider>
  );
}
